import {useEffect, useState, useCallback} from 'react';
import {debounce} from 'lodash';

/**
 * @template T
 * @param {T} value - Starting value of the data
 * @param {(v: T) => Promise<void>} saveFunction - Function to save the data
 * @param {boolean} active - set once value is stable to prevent saving on load.
 * @param {number} timeout - Autosave delay
 */
export function useAutoSave<T>(
    value: T,
    saveFunction: (T) => Promise<void>,
    active: boolean,
    timeout = 300
): {state: 'saved' | 'saving' | 'unsaved'; save: () => void} {
    const [state, setState] = useState<'saved' | 'saving' | 'unsaved'>('saved');

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedCall = useCallback(
        debounce(async (data: T) => {
            setState('saving');
            await saveFunction(data);
            setState('saved');
        }, timeout),
        [timeout, saveFunction]
    );

    useEffect(() => {
        if (active) {
            setState('unsaved');
            debouncedCall.cancel();
            debouncedCall(value);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, debouncedCall]);

    return {state, save: () => debouncedCall.flush()};
}
